import { useMemo } from 'react'
import { useAuth0 as useInternalAuth0 } from '@auth0/auth0-react'
import * as config from '../../config'

const CLIENT_ID = config.auth0.clientId
const CONNECTION = 'Saga-Customer'
const ENDPOINT_URL = `https://${config.auth0.domain}/dbconnections/change_password`

interface Auth0User {
  email: string
  email_verified: boolean
  name: string
  nickname: string
  picture: string
  sub: string
  updated_at: string
}

export interface User
  extends Omit<Auth0User, 'email_verified' | 'org_id' | 'updated_at'> {
  emailVerified: boolean
  updatedAt: string
}

export interface UseAuth0Result
  extends Omit<ReturnType<typeof useInternalAuth0>, 'user'> {
  user?: User
  changePassword: () => Promise<string>
}

export const useAuth0 = (): UseAuth0Result => {
  const { user: auth0User, ...rest } = useInternalAuth0<Auth0User>()
  const user = useMemo(() => {
    if (typeof auth0User === 'undefined') return undefined

    const {
      email_verified: emailVerified,
      updated_at: updatedAt,
      ...rest
    } = auth0User
    return {
      ...rest,
      emailVerified,
      updatedAt,
    }
  }, [auth0User])

  const auth0ChangePassword = async () => {
    const data = {
      client_id: CLIENT_ID,
      email: auth0User.email,
      connection: CONNECTION,
    }

    const requestParams = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
    const result = await fetch(ENDPOINT_URL, requestParams)
    const text = await result.text()
    if (result.ok) {
      return text
    } else {
      throw new Error(text)
    }
  }

  return {
    user,
    changePassword: auth0ChangePassword,
    ...rest,
  }
}

export { withAuthenticationRequired, Auth0Context } from '@auth0/auth0-react'
export { Auth0Provider } from './auth0-provider'
